/* eslint-disable @typescript-eslint/no-this-alias */
import CreateEdge from '@antv/g6-pc/lib/behavior/create-edge';
import { IG6GraphEvent } from '@antv/g6';
import { isFunction } from '@antv/util';
import { ItemState } from '../../../../constants';
import { shapeNames } from '../../../../shapes';
import { Node, Edge, NodeId, EdgeModel, Graph, Behavior } from '../../../../types';
import { guid } from '../../../../utils';

enum CreateEdgeTrigger {
  Click = 'click',
  Drag = 'drag',
}

enum CreateEdgeKey {
  Shift = 'shift',
  Ctrl = 'ctrl',
  Control = 'control',
  Alt = 'alt',
  Meta = 'meta',
}

type CreateEdgeType = typeof CreateEdge;

interface CustomCreateEdge extends Behavior {
  type: 'create-edge';
  /**
   * Specifies the trigger for creating an edge.
   * - `click`: the user is allowed to creat an edge by clicking two end nodes
   *   as source and target node respectively
   * - `drag`: the user is allowed to create an edge by 'dragging' from a source
   *   node to a target node.
   *   - NOTE: `drag` cannot create a self-loop edge
   */
  trigger: CreateEdgeTrigger;
  /**
   * The assistant trigger key from the keyboard.
   * If it is undefined or unset, only trigger decides the triggering
   * interaction from user.
   * Otherwise, this behavior will be triggered by trigger only when key is
   * pressed.
   */
  key?: CreateEdgeKey;
  /** Is `this.key` currently being pressed? */
  keydown?: boolean;
  /** Is an edge being added */
  addingEdge?: boolean;
  /** The edge being created */
  edge?: Edge | null;
  /** The node the edge originates from */
  source?: NodeId;
  /** The index of the node anchor in the source node  */
  sourceAnchor?: number | null;
  /** Configurations added to any edge that is created */
  edgeConfig: EdgeModel;
  /** Whether an edge can be created */
  canAddEdge(e: IG6GraphEvent): boolean;
  /** Is the edge attempting to connect to the node of its source */
  isCircular(e: IG6GraphEvent): boolean;
  /** Associate the edge with the target node */
  attachEdgeToTarget(target: NodeId): void;
  /** Cleanup any references so that they don't appear when creating an edge again */
  cleanupReferences(): void;
  /** Stop edge creation */
  cancelCreating(e: Partial<IG6GraphEvent>): void;

  /**
   * EVENTS TRIGGERING THE BEHAVIOR
   */
  onNodeDragEnter(e: IG6GraphEvent): void;
  onNodeDragLeave(e: IG6GraphEvent): void;
  handleDragStart(e: IG6GraphEvent): void;
  handleDragStop(e: IG6GraphEvent): void;
}

/**
 * There were conflicts with our implementation of 'create-edge', which appear
 * to stem from bugs in G6.
 * Many of the functions in here are directly copy/pasted from the G6 source
 * code and slightly tweaked.
 *
 * G6 Code: https://github.com/antvis/G6/blob/master/packages/pc/src/behavior/create-edge.ts
 */
export const getCreateEdgeConfig = (): CreateEdgeType & CustomCreateEdge => ({
  ...CreateEdge,
  type: 'create-edge',
  trigger: CreateEdgeTrigger.Drag,
  edgeConfig: {},
  getEvents() {
    return {
      'node:dragstart': 'onClick',
      'combo:dragstart': 'onClick',
      drag: 'updateEndPoint',
      'node:drop': 'onClick',
      'combo:drop': 'onClick',
      dragend: 'onDragEnd',
      'node:dragenter': 'onNodeDragEnter',
      'node:dragleave': 'onNodeDragLeave',
    };
  },

  shouldBegin: function shouldBegin(e): boolean {
    if (!e) return false;
    const { shape, item, target } = e;
    if (!shape || !target || !item) return false;

    // Do not draw the edge if it is from the in anchor
    if (shape.get('name') !== shapeNames.anchorOut) return false;

    const sourceAnchor = target.get('anchorPointIndex');
    const anchorEdges = (item as Node)
      .getOutEdges()
      .filter((edge: Edge) => edge.getModel().sourceAnchor === sourceAnchor);

    // Only draw the edge if the anchor contains no other edges
    return anchorEdges.length === 0;
  },

  canAddEdge: function canAddEdge(e: IG6GraphEvent): boolean {
    const self = this;
    const { item } = e;
    const { inputConnectors } = item?.getModel() || {};

    return (
      !!item &&
      // cannot connect an edge to the same node (no circular connections)
      !self.isCircular.call(self, e) &&
      // there needs to be at least one input connector
      Array.isArray(inputConnectors) &&
      inputConnectors.length > 0
    );
  },

  /* -------------------------------------------------------------------------- */
  /*                               EVENT CALLBACKS                              */
  /* -------------------------------------------------------------------------- */

  onClick: function onClick(e: IG6GraphEvent) {
    const self = this;

    if (self.key && !self.keydown) return;
    if (self.addingEdge && self.edge) {
      self.handleDragStop(e);
    } else {
      self.handleDragStart(e);
    }
  },
  /**
   * Triggered by: `drag`
   */
  updateEndPoint: function updateEndPoint(e: IG6GraphEvent) {
    const self = this;
    const { graph } = self;
    const { x, y } = e;

    if (self.key && !self.keydown) return;
    if (self.edge && self.edge.destroyed) self.cancelCreating({ item: self.edge });
    if (!self.source) return;

    // If the source node has been removed at this time, end adding edges
    if (!(graph as Graph).findById(self.source as string)) {
      self.addingEdge = false;
    } else if (self.addingEdge && self.edge) {
      // The end of the updated edge is the mouse position
      (graph as Graph).updateItem(self.edge, { target: { x, y } }, false);
    }
  },
  onNodeDragEnter: function onNodeDragEnter(e: IG6GraphEvent) {
    if (this.addingEdge && e.item) {
      this.graph?.setItemState(e.item, ItemState.HighLight, true);
    }
  },
  onNodeDragLeave: function onNodeDragEnter(e: IG6GraphEvent) {
    if (this.addingEdge && e.item) {
      this.graph?.setItemState(e.item, ItemState.HighLight, false);
    }
  },
  onDragEnd(e: IG6GraphEvent) {
    const self = this;
    if (self.key && !self.keydown) return;
    const { item } = e;
    if (!item || item.getID() === self.source || item.getType() !== 'node') {
      self.cancelCreating({ item: self.edge as Edge, x: e.x, y: e.y });
    }
  },

  /* -------------------------------------------------------------------------- */
  /*                                  UTILITIES                                 */
  /* -------------------------------------------------------------------------- */
  handleDragStart: function handleDragStart(e: IG6GraphEvent) {
    const self = this;

    if (!self?.shouldBegin?.call(self, e)) return;

    const { item: node, target, x, y } = e;

    if (!node) return;

    const nodeId = node.getModel().id;

    self.addingEdge = true;
    self.sourceAnchor = target.get('anchorPointIndex');
    self.source = nodeId;

    const edge = (self.graph as Graph).addItem(
      'edge',
      {
        id: guid(),
        source: self.source,
        target: { x, y },
        // The source anchor was changing to the closest anchor to the edge,
        // so we're manually setting it
        sourceAnchor: self.sourceAnchor,
        ...self.retrieveEdgeConfig({ source: nodeId, target: nodeId }),
      },
      false,
    );

    // Temporarily set the capture of this edge to false, so that events of other
    // elements will be triggered
    (edge as Edge).getKeyShape().set('capture', false);

    self.edge = edge as Edge;
  },
  handleDragStop: function handleDragStop(e: IG6GraphEvent) {
    const self = this;

    if (!self?.shouldEnd?.call(self, e)) return;

    const { item: node } = e;
    const nodeId = (node as Node).getModel().id;

    if (self.canAddEdge.call(self, e)) {
      self.attachEdgeToTarget(nodeId as string);
    } else {
      e.item = self.edge as Edge;
      self.cancelCreating(e);
    }

    // Restore the edge's capture
    if (self.edge && !self.edge?.destroyed) {
      self.edge.getKeyShape().set('capture', true);
    }

    self.cleanupReferences();
  },

  retrieveEdgeConfig: function retrieveEdgeConfig(args: { source: string; target: string }) {
    const { getEdgeConfig, edgeConfig } = this;
    return getEdgeConfig && isFunction(getEdgeConfig) ? getEdgeConfig(args) : edgeConfig;
  },
  isCircular: function isCircular({ item }: IG6GraphEvent) {
    const self = this;
    const itemId = item?.getID() || item?.getModel()?.id;
    return itemId === self.source;
  },
  /**
   * Cancel adding edge, deleting the edge, or specifying the end point
   */
  cancelCreating: function cancelCreating(e: IG6GraphEvent) {
    const self = this;

    if (self.key && !self.keydown) return;
    if (!e) return;

    const { graph } = self;
    const { item: currentEdge } = e;

    if (self.addingEdge && (self.edge === currentEdge || e.target?.isCanvas?.())) {
      if (self.edge && !self.edge.destroyed) {
        graph?.removeItem(self.edge, false);
      }
      self.edge = null;
      self.addingEdge = false;
    }
  },
  /**
   * Attach the edge delegate to a targeted node, and emit appropriate events
   */
  attachEdgeToTarget: function attachEdgeToTarget(target: NodeId) {
    const self = this;
    const { graph, edge, source } = self;

    if (!graph || !edge) return;

    graph.emit('beforecreateedge', {});
    graph.updateItem(
      edge,
      { target, ...self.retrieveEdgeConfig({ source, target }), targetAnchor: 0 },
      false,
    );

    if (graph.get('enabledStack')) {
      graph.pushStack('add', {
        before: {},
        after: { edges: [{ ...edge.getModel(), itemType: 'edge' }] },
      });
    }

    graph.emit('aftercreateedge', { edge });
  },
  /**
   * Clean any references so that they don't linger when dragging again
   */
  cleanupReferences: function cleanupReferences() {
    this.edge = null;
    this.sourceAnchor = null;
    this.addingEdge = false;
  },
});
